import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User as UserIcon } from './Icon';

const NAV_LINKS = [
  { to: '/garage', label: 'My Garage' },
  { to: '/faq', label: 'FAQ' },
];

const Logo: React.FC = () => (
  <Link to="/" className="flex items-center gap-2.5">
    <span className="w-7 h-7 border-[1.5px] border-ink1 rounded-md flex items-center justify-center">
      <span className="font-serif-italic text-[15px] text-ink1">P</span>
    </span>
    <span className="font-serif font-medium text-[18px] text-ink1">ParkProof</span>
  </Link>
);

/**
 * App frame. Desktop (md+) gets a persistent top nav bar: Logo · My Garage ·
 * FAQ · Profile. Mobile gets the same links behind a burger menu — except on
 * "/" and "/parking/:id", where HomePage supplies its own chrome (search bar
 * / detail sheet) and a second nav bar would just be redundant.
 */
export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const isHomeRoute = location.pathname === '/' || location.pathname.startsWith('/parking/');

  return (
    <div className="min-h-screen bg-page flex flex-col">
      {/* Desktop header */}
      <header className="hidden md:block sticky top-0 z-30 bg-page/95 backdrop-blur border-b border-rule">
        <div className="max-w-app mx-auto px-s6 h-16 flex items-center gap-s5">
          <Logo />
          <nav className="flex items-center gap-s5 ml-s4">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-[13px] font-sans font-medium text-ink2 hover:text-ink1 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="flex-1" />
          <Link
            to="/profile"
            className="w-9 h-9 rounded-pill border border-rule bg-card flex items-center justify-center hover:bg-tint transition-colors"
            aria-label="Profile"
          >
            <UserIcon size={18} color="#0F0F0E" />
          </Link>
        </div>
      </header>

      {/* Mobile header — hidden on home routes, which supply their own chrome */}
      {!isHomeRoute ? (
        <header className="md:hidden sticky top-0 z-30 bg-page/95 backdrop-blur border-b border-rule">
          <div className="px-s4 h-14 flex items-center gap-s3">
            <Logo />
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-9 h-9 rounded-pill border border-rule bg-card flex items-center justify-center"
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              <span className="flex flex-col gap-[3px]">
                <span className="block w-4 h-[1.5px] bg-ink1" />
                <span className="block w-4 h-[1.5px] bg-ink1" />
                <span className="block w-4 h-[1.5px] bg-ink1" />
              </span>
            </button>
          </div>
          {menuOpen ? (
            <nav className="border-t border-rule bg-page flex flex-col">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="px-s5 py-s3 text-[14px] font-sans font-medium text-ink1 border-b border-rule-soft"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/profile"
                onClick={() => setMenuOpen(false)}
                className="px-s5 py-s3 text-[14px] font-sans font-medium text-ink1"
              >
                Profile
              </Link>
            </nav>
          ) : null}
        </header>
      ) : null}

      <div className="flex-1 flex flex-col">{children}</div>
    </div>
  );
};
